import { useState, useEffect, useCallback } from 'react';
import { getPromptingStructure } from '../utils/dataUtils';

export const useDataPointManagement = (
  subGoalIndex,
  promptingType,
  criteria
) => {
  const [dataPoints, setDataPoints] = useState([
    createInitialDataPoint(subGoalIndex, promptingType, criteria),
  ]);
// console.log(dataPoints,'  dataPoints');
  function createInitialDataPoint(
    subGoalIndex,
    promptingType,
    criteriaItems = []
  ) {
    const initialCriteria = Array.isArray(criteriaItems)
      ? criteriaItems.map((item) => ({
          MeasurementType: item.MeasurementType,
          Target: item.Target,
          TargetDuration: item.TargetDuration,
          Variable: item.Variable,
          PromptingLevel: item.PromptingLevel,
          value: 0,
        }))
      : [];

    return {
      currentGoal: subGoalIndex ? subGoalIndex : 'main',
      value: '',
      trialsNumber: [0],
      trailsValue: {
        TrailsMin: '',
        TrailsMax: '',
      },
      selectedOptions: [], // Always initialize as an array
      abcData: {
        antecedent: '',
        behavior: '',
        consequence: '',
        setting: '',
        notes: '',
      },
      phaseChange: '',
      precedingBehavior: '',
      eventLine: '',
      timeOfCollection: '',
      timeOfCollectionSpecific: '',
      notes: '',
      sessionSummary: '',
      groupSize: '',
      sessionLength: '',
      cptCodes: '',
      prompting: getPromptingStructure(promptingType),
      criteria: initialCriteria,
      promptingLevel: '',
      physicalAssistanceLevel: '',
      taskAnalysis: {},
      additionalStaff: '',
      numberOfStaffs: 0,
      interventions: [],
      customSection: [],
    };
  }

  const handleDeleteDataPoint = useCallback((index) => {
    setDataPoints((prev) => {
      const newDataPoints = [...prev];
      newDataPoints.splice(index, 1);
      return newDataPoints;
    });
  }, []);

  const handleAddDataPoint = useCallback(() => {
    setDataPoints((prev) => [
      ...prev,
      createInitialDataPoint(subGoalIndex, promptingType, criteria),
    ]);
  }, [subGoalIndex, promptingType, criteria]);

  const handleTrialChange = useCallback((dataIndex, trialIndex, value) => {
    setDataPoints((prev) =>
      prev.map((point, i) =>
        i === dataIndex
          ? {
              ...point,
              trialsNumber: point.trialsNumber.map((trial, tIndex) =>
                tIndex === trialIndex ? value : trial
              ),
            }
          : point
      )
    );
  }, []);

  const handleAddTrial = useCallback((index, trialsMax) => {
    setDataPoints((prev) =>
      prev.map((point, i) => {
        if (i === index) {
          if (
            trialsMax !== 'N/A' &&
            point.trialsNumber.length >= Number(trialsMax)
          ) {
            return point;
          }
          return {
            ...point,
            trialsNumber: [...point.trialsNumber.map(Number), 0],
          };
        }
        return point;
      })
    );
  }, []);

  const handleCriteriaChange = useCallback(
    (dataIndex, criteriaIndex, value) => {
      setDataPoints((prev) =>
        prev.map((point, i) =>
          i === dataIndex
            ? {
                ...point,
                criteria: point?.criteria?.map((criterion, cIndex) =>
                  cIndex === criteriaIndex
                    ? { ...criterion, value: value }
                    : criterion
                ),
              }
            : point
        )
      );
    },
    []
  );

  const handleCheckboxChange = useCallback((index, event) => {
    const { value, checked } = event.target;
    setDataPoints((prev) =>
      prev.map((dp, i) => {
        if (i !== index) return dp;

        let updatedDataPoint = {
          ...dp,
          selectedOptions: checked
            ? [...dp.selectedOptions, value]
            : dp.selectedOptions.filter((option) => option !== value),
        };

        // Clear associated data when unchecking
        if (!checked) {
          switch (value) {
            case 'Add ABC Data':
              updatedDataPoint.abcData = {
                antecedent: '',
                behavior: '',
                consequence: '',
                setting: '',
                notes: '',
              };
              break;
            case 'Add Phase Change Line':
              updatedDataPoint.phaseChange = '';
              break;
            case 'Add Preceding Behavior':
              updatedDataPoint.precedingBehavior = '';
              break;
            case 'Add Event Line':
              updatedDataPoint.eventLine = '';
              break;
            case 'Add Time of Collection':
              updatedDataPoint.timeOfCollection = '';
              updatedDataPoint.timeOfCollectionSpecific = '';
              break;
            case 'Add Prompting Level':
              updatedDataPoint.promptingLevel = '';
              break;
            case 'Add Staff Present Information':
              updatedDataPoint.additionalStaff = '';
              updatedDataPoint.numberOfStaffs = 0;
              break;
            case 'Add CPT Code':
              updatedDataPoint.cptCodes = '';
              break;
            case 'Add Session Summary':
              updatedDataPoint.sessionSummary = '';
              break;
            case 'Add Group Size':
              updatedDataPoint.groupSize = '';
              break;
            case 'Add Session Length':
              updatedDataPoint.sessionLength = '';
              break;
            default:
              break;
          }
        }

        return updatedDataPoint;
      })
    );
  }, []);

  const handleTimeChange = useCallback((index, newTime) => {
    setDataPoints((prev) =>
      prev.map((dp, i) =>
        i === index
          ? {
              ...dp,
              timeOfCollection: newTime,
            }
          : dp
      )
    );
  }, []);

  const validateValue = useCallback((index, newValue) => {
    const parsedValue = Number.parseFloat(newValue);
    if (isNaN(parsedValue)) return;

    setDataPoints((prev) =>
      prev.map((dp, i) => (i === index ? { ...dp, value: newValue } : dp))
    );
  }, []);

  const handleFieldChange = useCallback(
    (index, field, newValue) => {
      if (field === 'value') {
        validateValue(index, newValue);
      } else {
        setDataPoints((prev) =>
          prev.map((dp, i) => (i === index ? { ...dp, [field]: newValue } : dp))
        );
      }
    },
    [validateValue]
  );

  const handleAbcChange = useCallback((index, e) => {
    const { name, value } = e.target;
    setDataPoints((prev) =>
      prev.map((dp, i) =>
        i === index ? { ...dp, abcData: { ...dp.abcData, [name]: value } } : dp
      )
    );
  }, []);

  const handlePromptingChange = useCallback((index, field, value) => {
    setDataPoints((prev) =>
      prev.map((dp, i) =>
        i === index
          ? {
              ...dp,
              prompting: { ...dp.prompting, [field]: value },
            }
          : dp
      )
    );
  }, []);

  const resetDataPoints = useCallback(() => {
    setDataPoints([
      createInitialDataPoint(subGoalIndex, promptingType, criteria),
    ]);
  }, [subGoalIndex, promptingType, criteria]);

  // Update dataPoints when criteria changes
  useEffect(() => {
    if (criteria && criteria.length > 0 && Array.isArray(criteria)) {
      setDataPoints((prev) =>
        prev.map((point) => ({
          ...point,
          criteria: criteria?.map((item) => ({
            MeasurementType: item.MeasurementType,
            Target: item.Target,
            TargetDuration: item.TargetDuration,
            Variable: item.Variable,
            value:
              point.criteria.find((c) => c.Variable === item.Variable)?.value ||
              0,
          })),
        }))
      );
    }
  }, [criteria]);

  return {
    dataPoints,
    setDataPoints,
    handleDeleteDataPoint,
    handleAddDataPoint,
    handleTrialChange,
    handleAddTrial,
    handleCriteriaChange,
    handleCheckboxChange,
    handleTimeChange,
    handleFieldChange,
    handleAbcChange,
    handlePromptingChange,
    resetDataPoints,
  };
};
